/**
 * ThemeService — manages palette ([data-theme]) and mode ([data-mode]) on <html>.
 *
 * THEME-SYSTEM-1 architecture (DESIGN-TOKENS-1 aligned):
 *   data-theme  = palette : 'heritage' | 'platform' | 'modern'   (localStorage: kairos-theme-cache)
 *   data-mode   = mode    : 'light'    | 'dark'                   (localStorage: kairos-mode-cache)
 *
 * Keys match the FOUC bootstrap in index.html so that the inline script and the
 * runtime store read/write the same source.
 *
 * Migration: reads legacy 'kairos-theme' key (prior mode-only store) on first
 * load; migrates mode value to new key, then abandons the legacy key.
 */

const PALETTE_KEY = 'kairos-theme-cache';
const MODE_KEY    = 'kairos-mode-cache';
const LEGACY_KEY  = 'kairos-theme';

const VALID_PALETTES = new Set(['heritage', 'platform', 'modern']);
const VALID_MODES    = new Set(['light', 'dark']);


/**
 * KAIROS-AESTHETIC-OWNER-1 / THEME-SYSTEM-EXCEPTION-FIX-1-SUB-2 — P-15 closure.
 * Replace silent throw-swallow with operator-visible warnings that include
 * a structured payload (op, key, error message) so the aesthetic owner can
 * surface theme-system failures during weekly regression sweeps.
 */
function logThemeException(op, key, err) {
  // Use console.warn rather than throw so user surfaces don't crash on
  // localStorage-disabled environments (private browsing, embedded webviews).
  try {
    console.warn('[KairosThemeException]', JSON.stringify({
      op,
      key,
      error: err && err.message ? err.message : String(err || 'unknown'),
    }));
  } catch {
    // Last-resort: console.warn itself failed (e.g., console replaced). Swallow.
  }
}

function readStored(key, valid, fallback) {
  try {
    const v = localStorage.getItem(key);
    if (valid.has(v)) return v;
  } catch (e) { logThemeException('readStored', key, e); }
  return fallback;
}

function systemPrefersDark() {
  try {
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  } catch (e) { logThemeException('systemPrefersDark', 'matchMedia', e); }
  return false;
}

function migrateLegacyKey() {
  try {
    const legacy = localStorage.getItem(LEGACY_KEY);
    if (VALID_MODES.has(legacy) && !localStorage.getItem(MODE_KEY)) {
      localStorage.setItem(MODE_KEY, legacy);
    }
  } catch (e) { logThemeException('migrateLegacyKey', LEGACY_KEY, e); }
}

export const ThemeService = {
  PALETTE_KEY,
  MODE_KEY,

  init() {
    migrateLegacyKey();
    const palette = readStored(PALETTE_KEY, VALID_PALETTES, 'heritage');
    const mode    = readStored(MODE_KEY, VALID_MODES, systemPrefersDark() ? 'dark' : 'light');
    this.applyPalette(palette);
    this.applyMode(mode);
  },

  applyPalette(p) {
    const palette = VALID_PALETTES.has(p) ? p : 'heritage';
    document.documentElement.setAttribute('data-theme', palette);
  },

  applyMode(m) {
    const mode = VALID_MODES.has(m) ? m : 'light';
    document.documentElement.setAttribute('data-mode', mode);
  },

  currentPalette() {
    const v = document.documentElement.getAttribute('data-theme');
    return VALID_PALETTES.has(v) ? v : 'heritage';
  },

  currentMode() {
    const v = document.documentElement.getAttribute('data-mode');
    return VALID_MODES.has(v) ? v : 'light';
  },

  setPalette(p) {
    this.applyPalette(p);
    try { localStorage.setItem(PALETTE_KEY, this.currentPalette()); } catch (e) { logThemeException('setPalette', PALETTE_KEY, e); }
    return this.currentPalette();
  },

  setMode(m) {
    this.applyMode(m);
    try { localStorage.setItem(MODE_KEY, this.currentMode()); } catch (e) { logThemeException('setMode', MODE_KEY, e); }
    return this.currentMode();
  },

  toggleMode() {
    const next = this.currentMode() === 'light' ? 'dark' : 'light';
    return this.setMode(next);
  },

  /** @deprecated — backward compat; returns currentMode() */
  current() { return this.currentMode(); },

  /** @deprecated — backward compat; delegates to toggleMode() */
  toggle() { return this.toggleMode(); },

  /** @deprecated — backward compat; delegates to setMode() */
  set(m) { return this.setMode(m); },
};

export default ThemeService;
